import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ProtossTokenNode - the Tiptap atom node that renders a `{{protoss:...}}` token
 * as a syntax-hidden TokenChip inside the editor. It is selectable/deletable as a
 * single unit and serializes back to `{{...}}` text (via getJSON ->
 * tiptapToDocumentChildren in @protoss/doc, and via renderText for plain text).
 *
 * `tokenizeTiptapDoc` converts `{{...}}` text in an incoming Tiptap doc into these
 * nodes on load, so existing tokens show as chips without a live plugin.
 */
import * as React from 'react';
import { clsx } from 'clsx';
import { mergeAttributes, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { TokenChip } from './TokenChip.js';
const TokenValueResolverContext = React.createContext(null);
export function TokenValueResolverProvider({ resolve, children, }) {
    return (_jsx(TokenValueResolverContext.Provider, { value: resolve, children: children }));
}
const TokenOverrideContext = React.createContext(null);
export function TokenOverrideProvider({ onOverride, children, }) {
    return (_jsx(TokenOverrideContext.Provider, { value: onOverride, children: children }));
}
const TOKEN_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;
const STOPWORDS = new Set(['of', 'and', 'the', 'to', 'for', 'a', 'an', 'in', 'on', 'or', 'by']);
function titleCase(words) {
    return words
        .filter(Boolean)
        .map((w, i) => {
        const lower = w.toLowerCase();
        if (i > 0 && STOPWORDS.has(lower))
            return lower;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
        .join(' ');
}
/** `context.clientName` / `asset:brandLogo` -> "Client Name" / "Brand Logo". */
export function humanizeTokenLabel(path) {
    const leaf = path.split(/[.:]/).pop() ?? path;
    return titleCase(leaf
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_\-.]+/g, ' ')
        .trim()
        .split(/\s+/));
}
/** Coarse source from a token path prefix (the editor has no catalog). */
export function inferTokenSource(path) {
    if (path.startsWith('asset:'))
        return 'asset';
    if (path.startsWith('item.') || path.startsWith('data.'))
        return 'data';
    if (path.startsWith('narrative.'))
        return 'ai_section';
    if (path.startsWith('context.') || path.startsWith('date.') || path.startsWith('brand.')) {
        return 'context';
    }
    return 'unknown';
}
function TokenNodeView({ node, editor }) {
    const token = String(node.attrs.token ?? '');
    const stripped = token.replace(/^protoss:/, '');
    const resolve = React.useContext(TokenValueResolverContext);
    const onOverride = React.useContext(TokenOverrideContext);
    // When a resolver is present and yields a value (deal SOW editor), show the resolved value -
    // highlighted when it is generated content - so the editor matches the document instead of a
    // raw `{{protoss:...}}` token. Otherwise (template authoring) fall back to the labelled chip.
    const resolved = resolve ? resolve(stripped) : null;
    const resolvedValue = resolved && 'value' in resolved ? resolved.value : '';
    // Edit-on-double-click as an OVERRIDE (not a literal replacement): the token stays a token,
    // but resolves to the user's value for EVERY occurrence, still highlighted, with an "Updated"
    // badge. An empty value clears the override (reset to the deal value). Local edit state only -
    // the commit goes through the host's persist handler, then the render context re-resolves.
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    // True while a set/clear override is persisting (the chip shows a spinner instead of the badge).
    const [pending, setPending] = React.useState(false);
    // True when the last persist FAILED (shows a retry hint instead of silently reverting).
    const [error, setError] = React.useState(false);
    const inputRef = React.useRef(null);
    const beginEdit = React.useCallback(() => {
        setError(false);
        setDraft(resolvedValue);
        setEditing(true);
    }, [resolvedValue]);
    React.useEffect(() => {
        if (editing)
            inputRef.current?.focus();
    }, [editing]);
    // Persist an override (or clear with null) and show an in-flight spinner until the host's
    // promise settles (it persists + reloads the render context, which re-resolves this token).
    // A rejection surfaces a retry hint rather than silently reverting (and is never left unhandled).
    const applyOverride = React.useCallback(async (value) => {
        if (!onOverride)
            return;
        setPending(true);
        setError(false);
        try {
            await onOverride(stripped, value);
        }
        catch {
            setError(true);
        }
        finally {
            setPending(false);
        }
    }, [onOverride, stripped]);
    const commit = React.useCallback(() => {
        setEditing(false);
        const next = draft.trim();
        if (!onOverride)
            return;
        if (next === '')
            void applyOverride(null);
        else if (next !== resolvedValue)
            void applyOverride(next);
    }, [draft, onOverride, resolvedValue, applyOverride]);
    // A resolver is present but the value isn't ready yet (the deal render context is still
    // loading) - show a spinner inside the chip instead of the static label, so it reads as
    // "filling in" rather than a permanent placeholder.
    if (resolved && 'loading' in resolved) {
        return (_jsxs(NodeViewWrapper, { as: "span", className: "rte-token rte-token--loading", contentEditable: false, "data-protoss-token": token, title: `Loading {{${token}}}…`, children: [_jsx("span", { className: "rte-token__spinner", "aria-hidden": "true" }), _jsx("span", { className: "rte-token__sr", children: "Loading\u2026" })] }));
    }
    if (resolved) {
        const editable = !!editor?.isEditable;
        const overridable = editable && !!onOverride;
        const updated = !!resolved.updated;
        if (editing) {
            return (_jsx(NodeViewWrapper, { as: "span", className: "rte-token rte-token--editing", contentEditable: false, "data-protoss-token": token, children: _jsx("input", { ref: inputRef, className: "rte-token__input", value: draft, size: Math.max(draft.length + 1, 4), "aria-label": `Edit ${humanizeTokenLabel(stripped)}`, onChange: (e) => setDraft(e.target.value), onMouseDown: (e) => e.stopPropagation(), onKeyDown: (e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            commit();
                        }
                        else if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditing(false);
                        }
                    }, onBlur: commit }) }));
        }
        const annotated = updated || pending || error;
        return (_jsxs(NodeViewWrapper, { as: "span", className: clsx('rte-token', annotated && 'rte-token--annotated', overridable && 'rte-token--editable'), contentEditable: false, "data-protoss-token": token, "data-rte-tooltip": !overridable
                ? `Auto-filled: {{${token}}}`
                : updated
                    ? `Edited value. Double-click to change.`
                    : `Auto-filled from the deal. Double-click to edit.`, onDoubleClick: overridable && !pending ? beginEdit : undefined, children: [_jsx("span", { className: clsx('rte-token__value', resolved.highlight && 'rte-token--gen'), children: resolved.value }), pending ? (_jsx("span", { className: "rte-token__spinner rte-token__spinner--inline", "aria-hidden": "true" })) : error ? (_jsx("span", { className: "rte-token__error", "data-rte-tooltip": "Save failed - double-click to retry", "aria-label": "Save failed", children: "!" })) : updated ? (_jsx("button", { type: "button", className: "rte-token__updated", contentEditable: false, "data-rte-tooltip": "Edited - click to reset to the deal value", onMouseDown: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }, onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void applyOverride(null);
                    }, children: "Updated" })) : null] }));
    }
    const label = String(node.attrs.label || humanizeTokenLabel(stripped));
    const source = node.attrs.source || inferTokenSource(stripped);
    return (_jsx(NodeViewWrapper, { as: "span", className: "token-chip-nodeview", children: _jsx(TokenChip, { label: label, source: source }) }));
}
export const ProtossTokenNode = Node.create({
    name: 'protossToken',
    inline: true,
    group: 'inline',
    atom: true,
    selectable: true,
    addAttributes() {
        return {
            token: { default: '' },
            label: { default: '' },
            source: { default: 'unknown' },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-protoss-token]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, { 'data-protoss-token': '' }),
            `{{${HTMLAttributes.token ?? ''}}}`,
        ];
    },
    renderText({ node }) {
        return `{{${node.attrs.token}}}`;
    },
    addNodeView() {
        return ReactNodeViewRenderer(TokenNodeView);
    },
});
/** JSON for one token node from its inner path (kept with the `protoss:` prefix).
 *  Carries over the source text node's marks (e.g. a user highlight covering the token). */
function tokenNodeJSON(inner, marks) {
    const stripped = inner.replace(/^protoss:/, '');
    const node = {
        type: 'protossToken',
        attrs: {
            token: inner,
            label: humanizeTokenLabel(stripped),
            source: inferTokenSource(stripped),
        },
    };
    if (Array.isArray(marks) && marks.length > 0)
        node.marks = marks;
    return node;
}
function splitInline(node) {
    if (node.type === 'text' && typeof node.text === 'string' && node.text.includes('{{')) {
        const out = [];
        const text = node.text;
        let last = 0;
        for (const m of text.matchAll(TOKEN_RE)) {
            const start = m.index ?? 0;
            if (start > last)
                out.push({ ...node, text: text.slice(last, start) });
            out.push(tokenNodeJSON((m[1] ?? '').trim(), node.marks));
            last = start + m[0].length;
        }
        if (last < text.length)
            out.push({ ...node, text: text.slice(last) });
        return out.length > 0 ? out : [node];
    }
    if (Array.isArray(node.content)) {
        return [{ ...node, content: node.content.flatMap(splitInline) }];
    }
    return [node];
}
/** Replace `{{...}}` text with token nodes throughout a Tiptap doc (immutable). */
export function tokenizeTiptapDoc(doc) {
    if (!doc || !Array.isArray(doc.content))
        return doc;
    const d = doc;
    return { ...doc, content: (d.content ?? []).flatMap(splitInline) };
}
//# sourceMappingURL=tokenNode.js.map